
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Textarea } from '@/components/ui/textarea';

interface ProfileFormProps {
  initialData?: {
    full_name?: string;
    email?: string;
    bio?: string;
    job_title?: string;
    company?: string;
    whatsapp_number?: string;
  };
}

export default function ProfileForm({ initialData }: ProfileFormProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      full_name: initialData?.full_name || '',
      email: initialData?.email || '',
      bio: initialData?.bio || '',
      job_title: initialData?.job_title || '',
      company: initialData?.company || '',
      whatsapp_number: initialData?.whatsapp_number || '',
    }
  });
  
  const onSubmit = async (data: any) => {
    if (!user) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para atualizar seu perfil",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Format WhatsApp number by removing any non-digit characters
      const formattedWhatsApp = data.whatsapp_number.replace(/\D/g, '');
      
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name,
          email: data.email,
          bio: data.bio,
          job_title: data.job_title,
          company: data.company,
          whatsapp_number: formattedWhatsApp,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram atualizadas com sucesso",
      });
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      toast({
        title: "Erro",
        description: error.message || "Ocorreu um erro ao atualizar seu perfil",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Perfil</CardTitle>
        <CardDescription>
          Atualize suas informações pessoais
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Nome Completo</Label>
            <Input
              id="full_name"
              {...register("full_name", { required: "Nome é obrigatório" })}
            />
            {errors.full_name && (
              <p className="text-sm text-red-500">{errors.full_name.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              disabled
              {...register("email")}
            />
            <p className="text-xs text-gray-500">O email não pode ser alterado</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="whatsapp_number">WhatsApp</Label>
            <Input
              id="whatsapp_number"
              placeholder="Ex: 5511999999999"
              {...register("whatsapp_number")}
            />
            <p className="text-xs text-gray-500">Apenas números, formato: 5511999999999</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bio">Biografia</Label>
            <Textarea
              id="bio"
              placeholder="Conte um pouco sobre você..."
              {...register("bio")}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="job_title">Cargo</Label>
            <Input
              id="job_title"
              placeholder="Seu cargo atual"
              {...register("job_title")}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input
              id="company"
              placeholder="Empresa onde trabalha"
              {...register("company")}
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Salvando..." : "Salvar alterações"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
